import { orange } from '@mui/material/colors';
import { Editor } from '@tiptap/core';
import { TFunction } from 'i18next';
import { Node } from '@tiptap/pm/model';
import {
  ValidationInput,
  ValidationResult,
  ValidationRule,
  HighlightInstruction,
  KnownValidationRuleId,
} from './ReportMessageField.types';
import {
  CATCHALL_STOPWORDS,
  CAUSE_STOPWORDS,
  EVACUATION_STOPWORDS,
  FIRST_PERSON_STOPWORDS,
  INJURY_STOPWORDS,
  POLITICAL_STOP_WORDS,
} from './ReportMessageField.constants';

const pronounsAllowingApostrophe = new Set([
  'i',
  'me',
  'my',
  'mine',
  'myself',
  'we',
  'us',
  'our',
  'ours',
  'ourselves',
]);

/**
 * Builds a sophisticated regex part for a single keyword, handling
 * special cases like apostrophes, quotes, and ampersands.
 */
const buildRegexPart = (keyword: string): string => {
  const lowerKeyword = keyword.toLowerCase();

  // We replace negative lookbehind (?<!\w) with (?:^|\W) for iOS 15 compatibility,
  // as lookbehind assertions are not supported in Safari < 16.4.
  // https://caniuse.com/js-regexp-lookbehind
  if (lowerKeyword === '&') {
    return '(?:^|\\W)(&)(?!\\w)';
  }
  // Match a keyword when:
  // 1. Not preceded by a word character
  // 2. Either enclosed in quotes/parentheses or standalone
  // 3. If standalone, not followed by a letter or disallowed punctuation
  const isPronoun = pronounsAllowingApostrophe.has(lowerKeyword);
  const apostropheChars = [
    '\u0027', // Apostrophe
    '\u2019', // Right single quotation mark
    '\u02BC', // Modifier letter apostrophe
  ];
  const apostropheSuffix = `[${apostropheChars.join('')}](?:m|s|ve|re|ll)\\b`;
  const validWordSuffixes = ['.', '!', '?', ',', ';', ':', '"', '\\]', ')'];
  const wordSuffix = `[^\\s${validWordSuffixes.join('')}]`;

  const keywordPattern = isPronoun
    ? `(?:"${keyword}"|\\(${keyword}\\)|${keyword}(?:${apostropheSuffix}|(?!${wordSuffix})))`
    : `(?:"${keyword}"|\\(${keyword}\\)|${keyword}(?!${wordSuffix}))`;

  // Use (?:^|\W) instead of \b for consistency across all keywords.
  // While \b works for word characters, it fails for special characters like &.
  // Using the same pattern for all keywords keeps the match structure uniform
  // (prefix + captured keyword), simplifying downstream extraction logic (validation and highlighting).
  return `(?:^|\\W)(${keywordPattern})`;
};

/**
 * Creates a case-insensitive, global RegExp from a list of keywords
 * using the sophisticated `buildRegexPart` logic.
 */
export const createComplexKeywordsRegex = (keywords: string[]): RegExp => {
  const regexParts = keywords
    .map((keyword) => keyword.trim())
    .filter(Boolean)
    .map(buildRegexPart);
  // The 'gi' flags are crucial:
  // 'g' for global (find all matches, not just the first)
  // 'i' for case-insensitive
  return new RegExp(regexParts.join('|'), 'gi');
};

const FPS_REGEX = createComplexKeywordsRegex([
  'forward progress stopped',
  'stopped',
  'FPS',
]);
const FIRST_PERSON_REGEX = createComplexKeywordsRegex(FIRST_PERSON_STOPWORDS);
const EVACUATION_REGEX = createComplexKeywordsRegex(EVACUATION_STOPWORDS);
const CATCHALL_REGEX = createComplexKeywordsRegex(CATCHALL_STOPWORDS);
const INJURY_REGEX = createComplexKeywordsRegex(INJURY_STOPWORDS);
const CAUSE_REGEX = createComplexKeywordsRegex(CAUSE_STOPWORDS);
const POLITICAL_REGEX = createComplexKeywordsRegex(POLITICAL_STOP_WORDS);
const ALERT_CA_REGEX = createComplexKeywordsRegex([
  'alert ca',
  'alert california',
]);
const ACREAGE_REGEX = createComplexKeywordsRegex(['acre', 'acres', 'acreage']);
const CONTAINMENT_REGEX = createComplexKeywordsRegex([
  'contained',
  'containment',
]);
const HASHTAG_REGEX = /#[a-zA-Z]\w*/gi;
const ELLIPSES_REGEX = /\.{3}|…/gi;
const STARTS_WITH_PER_REGEX = /^\s*per/i;

// Note: When using global regexes in conditions with .test(), always clone them
// to avoid polluting the shared regex instance. Global regexes maintain lastIndex
// state, and .test() mutates it, causing matchAll() to start from the wrong position.
// Pattern: new RegExp(REGEX.source, REGEX.flags).test(report)
const getValidationRules = (): ValidationRule[] => [
  {
    id: 'fps',
    condition: ({ fpsChecked }) => !fpsChecked,
    messageKey: 'addIncidentReport.inputs.description.warnings.fps',
    regex: FPS_REGEX,
  },
  {
    id: 'fpsSilent',
    condition: ({ isSilent }) => Boolean(isSilent),
    messageKey: 'addIncidentReport.inputs.description.warnings.fpsSilent',
    regex: FPS_REGEX,
  },
  {
    id: 'firstPersonPronouns',
    condition: () => true,
    messageKey:
      'addIncidentReport.inputs.description.warnings.firstPersonPronouns',
    regex: FIRST_PERSON_REGEX,
  },
  {
    id: 'evacuation',
    condition: ({ isSilent }) => Boolean(isSilent),
    messageKey: 'addIncidentReport.inputs.description.warnings.evacuation',
    regex: EVACUATION_REGEX,
  },
  {
    id: 'reportStartWith',
    condition: ({ report }) =>
      new RegExp(
        STARTS_WITH_PER_REGEX.source,
        STARTS_WITH_PER_REGEX.flags,
      ).test(report),
    messageKey: 'addIncidentReport.inputs.description.warnings.reportStartWith',
    regex: STARTS_WITH_PER_REGEX,
  },
  {
    id: 'socialHashtags',
    condition: ({ report }) =>
      new RegExp(HASHTAG_REGEX.source, HASHTAG_REGEX.flags).test(report),
    messageKey: 'addIncidentReport.inputs.description.warnings.socialHashtags',
    regex: HASHTAG_REGEX,
  },
  {
    id: 'catchall',
    condition: () => true,
    messageKey: 'addIncidentReport.inputs.description.warnings.catchall',
    regex: CATCHALL_REGEX,
  },
  {
    id: 'injuryFatality',
    condition: () => true,
    messageKey: 'addIncidentReport.inputs.description.warnings.injuryFatality',
    regex: INJURY_REGEX,
  },
  {
    id: 'cause',
    condition: () => true,
    messageKey: 'addIncidentReport.inputs.description.warnings.cause',
    regex: CAUSE_REGEX,
  },
  {
    id: 'political',
    condition: () => true,
    messageKey: 'addIncidentReport.inputs.description.warnings.political',
    regex: POLITICAL_REGEX,
  },
  {
    id: 'ellipses',
    condition: ({ report }) =>
      new RegExp(ELLIPSES_REGEX.source, ELLIPSES_REGEX.flags).test(report),
    messageKey: 'addIncidentReport.inputs.description.warnings.ellipses',
    regex: ELLIPSES_REGEX,
  },
  {
    id: 'alertCA',
    condition: () => true,
    messageKey: 'addIncidentReport.inputs.description.warnings.alertCA',
    regex: ALERT_CA_REGEX,
  },
  {
    id: 'acreage',
    condition: ({ acreageChanged }) => !acreageChanged,
    messageKey: 'addIncidentReport.inputs.description.warnings.acreage',
    regex: ACREAGE_REGEX,
  },
  {
    id: 'containment',
    condition: ({ containmentChanged }) => !containmentChanged,
    messageKey: 'addIncidentReport.inputs.description.warnings.containment',
    regex: CONTAINMENT_REGEX,
  },
];

/**
 * Extract the actual keyword from a regex match.
 * For regexes with capture groups (from buildRegexPart), the full match includes
 * a prefix character (space, start-of-string, etc.), but the keyword is in a
 * capture group. For regexes without capture groups, use the full match.
 */
export const extractKeywordFromMatch = (match: RegExpMatchArray): string => {
  return match.length > 1
    ? match.slice(1).find((g) => g !== undefined) || ''
    : match[0] || '';
};

export const getReportValidationResult = (
  input: {
    t: TFunction;
  } & ValidationInput,
): ValidationResult => {
  const { rulesToSkip = [], t, customRules = [], evaluationInputData } = input;
  const { report } = evaluationInputData;

  if (!report) return { messageWarnings: [], highlightInstructions: [] };

  const messageWarnings: string[] = [];
  const highlightInstructions: HighlightInstruction = [];

  [
    ...customRules,
    ...getValidationRules().filter(
      (rule) => !rulesToSkip.includes(rule.id as KnownValidationRuleId),
    ),
  ].forEach((rule) => {
    const matches = rule.regex.global
      ? Array.from(report.matchAll(rule.regex))
      : ([report.match(rule.regex)].filter(Boolean) as RegExpMatchArray[]);

    const currentMatches = matches.map(extractKeywordFromMatch).filter(Boolean);

    // A rule is active if it finds matches AND its condition passes.
    if (currentMatches.length > 0 && rule.condition(evaluationInputData)) {
      messageWarnings.push(
        t(rule.messageKey, { word: currentMatches.join(', ') }),
      );
      highlightInstructions.push(rule.regex);
    }
  });

  return { messageWarnings, highlightInstructions };
};

/**
 * Maps a string index from `doc.textBetween` back into
 * a ProseMirror document position.
 *
 * This function walks the document text nodes, accumulates their lengths,
 * and finds the correct ProseMirror document position that corresponds to
 * a given string index.
 */
const getDocPosFromIndex = (doc: Node, index: number): number => {
  let pos = 0;
  let found: number | null = null;

  doc.descendants((node, nodePos) => {
    if (found !== null) return false;
    if (node.isText && node.text) {
      const len = node.text.length;
      if (index <= pos + len) {
        found = nodePos + index - pos;
        // Stop the descent
        return false;
      }
      pos += len;
    }
    return true;
  });

  return found ?? 0;
};

export const applyHighlights = (
  editor: Editor,
  instructions: HighlightInstruction,
): void => {
  const { doc, tr } = editor.state;
  const decorationMarkType = editor.schema.marks.decoration;

  // Clear all previous highlights
  tr.removeMark(0, doc.content.size, decorationMarkType);

  if (instructions.length === 0) {
    if (tr.steps.length > 0) editor.view.dispatch(tr);
    return;
  }

  instructions.forEach((regex) => {
    if (!regex.global) {
      // We only care about the first match in the entire document
      const fullText = doc.textBetween(0, doc.content.size, '\n');
      const match = fullText.match(regex);

      if (match && match.index !== undefined) {
        const capturedGroup = extractKeywordFromMatch(match);

        if (capturedGroup) {
          const prefixLength = match[0].length - capturedGroup.length;
          const from = getDocPosFromIndex(doc, match.index + prefixLength);
          const to = from + capturedGroup.length;
          const mark = decorationMarkType.create({ color: orange[500] });
          tr.addMark(from, to, mark);
        }
      }
    } else {
      // We need to walk nodes, because global regex must find all matches
      doc.descendants((node, pos) => {
        if (!node.isText || !node.text) return;

        // Clone the regex to prevent shared state issues with global regexes.
        // Global regexes mutate `lastIndex` when using .exec in a loop.
        // Cloning ensures each search starts fresh, which is necessary here
        // since we reuse the same regex instances for validation.
        // Note: string.match(regex) is unaffected because it ignores lastIndex
        // and always begins searching from index 0.
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/Symbol.match#description
        const clonedRegex = new RegExp(regex.source, regex.flags);

        let match;
        // eslint-disable-next-line no-cond-assign
        while ((match = clonedRegex.exec(node.text)) !== null) {
          if (typeof match.index === 'number') {
            const capturedGroup = extractKeywordFromMatch(match);

            if (capturedGroup) {
              const prefixLength = match[0].length - capturedGroup.length;
              const from = pos + match.index + prefixLength;
              const to = from + capturedGroup.length;
              const mark = decorationMarkType.create({ color: orange[500] });
              tr.addMark(from, to, mark);
            }
          }
        }
      });
    }
  });

  if (tr.steps.length > 0) {
    editor.view.dispatch(tr);
  }
};

export const removeDecorationMarks = (html: string): string => {
  return html.replace(/<\/?mark\b[^>]*>/g, '');
};
